import React, { Dispatch, useEffect, useMemo, useRef, useState } from "react";
import { OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { useFieldArray, useForm, useFormContext, FormProvider, useWatch, get } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestionCircle } from "@fortawesome/free-solid-svg-icons";
import { useSearchParams } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";

import "./organisationForm.scss";
import { useGetFreightModesConfig } from "api/queries/location/useGetFreightModesConfig";
import { FormStepTypes } from "constants/";
import testIds from "constants/testIds";
import { RequestFormAction, RequestFormActionType, RequestFormState } from "pages/shipper/RequestForm/RequestFormState";
import { getObjectByKeys } from "helpers/commonHelpers";
import Button from "components/atoms/Button/Button";
import Alert from "components/v2/Alert/Alert";
import ButtonV2 from "components/v2/Button/Button";
import usePrevValue from "hooks/usePrevValue";
import { checkForRemovedFreightModes } from "./OrganisationFormHelpers";
import SearchSelect from "../Common/SearchSelect";
import FormItemInfo from "../Common/FormItemInfo";
import useGetMultiDrop from "../Schedule/hooks/useGetMultiDrop";

type OrganisationFormFreightModeProps = {
  state: RequestFormState;
  dispatch: Dispatch<RequestFormAction>;
  isNewShipment?: boolean;
  isLoadingShipmentBasic?: boolean;
  isConsolidated?: boolean;
};

function OrganisationFormFreightMode(props: OrganisationFormFreightModeProps) {
  const intl = useIntl();
  const { state, dispatch, isNewShipment, isLoadingShipmentBasic, isConsolidated } = props;
  const {
    getValues,
    setValue,
    control,
    clearErrors,
    formState: { errors },
  } = useFormContext();
  const methods = useForm<any>({
    defaultValues: {
      freight_modes: [{ value: "" }],
    },
  });
  const { fields, append, remove } = useFieldArray({ name: "freight_modes", control: methods.control });
  const { isMultiDropQuoteRequest } = useGetMultiDrop();
  const invitedModes = useWatch({ name: "invited_modes", control });
  const selectedFreightModes = useWatch({ name: "freight_modes", control: methods.control });
  const paymentLocation = getValues("organizational.location");
  const isManuallyUpdated = useRef<boolean>(false);
  const [preselectFreightmodes, setPreselectFreightmodes] = useState<boolean>(false);
  const [searchParams] = useSearchParams();
  const currentActiveStep = searchParams.get("active_step");
  const isDuplicated = searchParams.get("is_duplicated");
  const prevActiveStep = usePrevValue(currentActiveStep);

  // WEB-8975: We want to show a warning when a user
  // edits a quote request and removes a freight modes
  const initialInvitedModes = getValues("initial_invited_modes");
  const hasRemovedFreightModes = !isDuplicated && checkForRemovedFreightModes(initialInvitedModes, invitedModes);

  const undoRemoval = () => {
    if (!initialInvitedModes || Object.keys(initialInvitedModes ?? {}).length === 0) return;
    setValue("invited_modes", initialInvitedModes);
  };

  const selectedFreightsHashMap = useMemo<Record<string, any>>(() => {
    const freightModesWithValues = selectedFreightModes.filter((val: any) => val.value);
    return getObjectByKeys(freightModesWithValues, "value");
  }, [selectedFreightModes]);

  const hasNoneMultiDropSelected = Object.entries(invitedModes ?? {}).some(
    ([key, value]) =>
      key !== "road_freight" &&
      Boolean(value) &&
      key !== "road_freight_option" &&
      value !== "multi-drop" &&
      !Array.isArray(value),
  );

  const roadTypeOptions = state.freightModes?.available_modes?.road_freight?.options ?? [];
  const roadTypeOptionsFiltered =
    hasNoneMultiDropSelected || isConsolidated
      ? roadTypeOptions?.filter((option: any) => option.value !== "multi-drop")
      : roadTypeOptions;

  const freightModes = useMemo(() => {
    if (state.freightModes.available_modes) {
      return Object.values(state.freightModes.available_modes);
    }
    return [];
  }, [state.freightModes.available_modes]);

  const setDefaultSelection = (freightModes: any) => {
    clearErrors("invited_modes");
    freightModes.forEach((mode: any) => {
      if (mode.value.includes("custom")) {
        const { index, key } = getCustomFreightIndex(mode.value);
        setValue(`invited_modes.custom.${index}`, { key: +key, value: true });
      } else {
        setValue(`invited_modes.${mode.value}`, true);
      }
      if (mode.value === "road_freight") {
        const preselectedOption = mode.options.find((option: any) => option.pre_select);
        if (preselectedOption) setValue("invited_modes.road_freight_option", preselectedOption.value);
      }
    });
  };

  const { data, isSuccess, isFetching } = useGetFreightModesConfig(paymentLocation);
  const isLoading = isFetching || isLoadingShipmentBasic;

  // Handle fetch freightModesConfig success
  useEffect(() => {
    if (isSuccess && data) {
      const updatedData = { ...data };
      if (updatedData.freight_modes.pre_select_freight_mode) {
        setPreselectFreightmodes(true);
        setDefaultSelection(updatedData.freight_modes.available_modes);
      } else {
        setPreselectFreightmodes(false);
      }
      // TODO: remove use of any
      const fetchedAvailableModes = getObjectByKeys(updatedData.freight_modes.available_modes, "value") as any;
      dispatch({
        type: RequestFormActionType.FREIGHT_MODE_REQUEST_SUCCESS,
        payload: {
          ...updatedData.freight_modes,
          available_modes: getObjectByKeys(updatedData.freight_modes.available_modes, "value"),
        },
      });
      updatedSelectedFreightModes(fetchedAvailableModes);
    }
  }, [isSuccess, data]);

  useEffect(() => {
    if (isManuallyUpdated.current) {
      isManuallyUpdated.current = false;
      return;
    }
    if (invitedModes && Object.keys(invitedModes).length) {
      const updatedValues: Array<{ value: string }> = [];
      Object.keys(invitedModes).forEach((key: string) => {
        if (typeof invitedModes[key] === "boolean" && !key.includes("custom") && invitedModes[key]) {
          updatedValues.push({ value: key });
        }
        if (key === "custom" && Array.isArray(invitedModes[key])) {
          invitedModes[key].forEach((item: any) => {
            if (item.value) {
              updatedValues.push({ value: `custom_${item.key}` });
            }
          });
        }
      });
      if (updatedValues.length) {
        methods.setValue("freight_modes", updatedValues);
      } else {
        methods.setValue("freight_modes", [{ value: "" }]);
      }
    }
  }, [invitedModes]);

  useEffect(() => {
    if (prevActiveStep === FormStepTypes.ORGANISATION && currentActiveStep !== FormStepTypes.ORGANISATION) {
      const freightModesWithValues = selectedFreightModes.filter((item: any) => item.value);
      if (!freightModesWithValues.length) {
        methods.setValue("freight_modes", [{ value: "" }]);
      } else {
        methods.setValue("freight_modes", freightModesWithValues);
      }
    }
  }, [prevActiveStep, currentActiveStep]);

  const handleFreightModeSelectChange = (val: any, idx: number) => {
    isManuallyUpdated.current = true;
    const prevValue = selectedFreightModes?.[idx]?.value;
    if (val?.[0]?.value) {
      clearErrors("invited_modes");
      if (val[0].value.includes("custom")) {
        const { index, key } = getCustomFreightIndex(val[0].value);
        setValue(`invited_modes.custom.${index}`, { key: +key, value: true });
      } else {
        setValue(`invited_modes.${val[0].value}`, true);
      }
    }
    if (prevValue) {
      if (prevValue === "road_freight") {
        setValue("invited_modes.road_freight_option", "");
      }
      if (prevValue.includes("custom")) {
        const { index, key } = getCustomFreightIndex(prevValue);
        setValue(`invited_modes.custom.${index}`, { key: +key, value: false });
      } else {
        setValue(`invited_modes.${prevValue}`, false);
      }
    }
  };

  const getFreightModeOptions = (index: number) =>
    freightModes.filter(
      (item: any) => selectedFreightModes?.[index]?.value === item.value || !selectedFreightsHashMap[item.value],
    );

  const getCustomFreightIndex = (value: string) => {
    const [, key] = value.split("_");
    const existingCustomItems = getValues("invited_modes.custom") || [];
    const existingValueIndex = existingCustomItems.findIndex((item: any) => +item.key === +key);
    return {
      key,
      index: existingValueIndex === -1 ? existingCustomItems.length : existingValueIndex,
    };
  };

  /**
   * if payment location is changed and there are some selected
   * freight modes which are not available in newly selected location
   * we will unselect them.
   */
  const updatedSelectedFreightModes = (availableFreightModes: any) => {
    if (!invitedModes) return;
    Object.keys(invitedModes).forEach((key: string) => {
      if (
        typeof invitedModes[key] === "boolean" &&
        !key.includes("custom") &&
        invitedModes[key] &&
        !availableFreightModes[key]
      ) {
        setValue(`invited_modes.${key}`, false);
      }
      if (key === "custom" && Array.isArray(invitedModes[key])) {
        invitedModes[key].forEach((item: any) => {
          if (item.value && !availableFreightModes[`custom_${item.key}`]) {
            const { index, key } = getCustomFreightIndex(`custom_${item.key}`);
            setValue(`invited_modes.custom.${index}`, { key: +key, value: false });
          }
        });
      }
    });
  };

  return (
    <div className="flex-row mt-24 mb-24 organisation-form-row">
      <div className="w-200 flex-shrink-0">
        <FormItemInfo
          labelId="new-request.organisational-details.freightMode"
          label="Freight mode"
          descId="new-request.organisational-details.freightModeInfo"
          description="Choose the freight mode"
        />
      </div>
      <div className="w-100">
        {isLoading && (
          <div className="skeleton-card">
            <div>
              <Row className="skeleton-row">
                <span key={1} className="skeleton" />
                <span key={2} className="skeleton" />
                <span key={3} className="skeleton" />
                <span key={4} className="skeleton" />
              </Row>
            </div>
          </div>
        )}
        {state.freightModes?.available_modes &&
          !isLoading &&
          fields.map((field: any, index) => (
            <div
              key={field.id}
              className={`freight-mode-container flex-row align-items-center ${index !== 0 ? "mt-3" : ""}`}
              data-testid="freight-mode-container"
            >
              <FormProvider {...methods}>
                <div className="d-flex flex-row align-items-center">
                  <div className="w-192">
                    <div className="d-flex align-items-center">
                      <SearchSelect
                        options={getFreightModeOptions(index)}
                        required
                        name={`freight_modes.${index}.value`}
                        testId={`${testIds.REQUEST_FORM_ORGANISATION_FREIGHT_MODE}-${index}`}
                        classes="flex-fill"
                        onSelect={(val: any) => handleFreightModeSelectChange(val, index)}
                        disable={preselectFreightmodes}
                        preselect={false}
                      />
                      {selectedFreightModes?.[index]?.value &&
                        state.freightModes?.available_modes?.[selectedFreightModes?.[index].value]?.help_text && (
                          <div className={`pl-2  pr-2 ${index === 0 ? "first-info" : ""}`}>
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip id={`tooltip-freight_modes-${index}`}>
                                  {state.freightModes?.available_modes?.[selectedFreightModes?.[index].value]?.help_text}
                                </Tooltip>
                              }
                            >
                              <FontAwesomeIcon icon={faQuestionCircle} className="font-b1 mt-1" />
                            </OverlayTrigger>
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              </FormProvider>
              <div className="d-flex align-items-end">
                {selectedFreightModes?.[index]?.value === "road_freight" && (
                  <SearchSelect
                    options={roadTypeOptionsFiltered}
                    name="invited_modes.road_freight_option"
                    testId={testIds.REQUEST_FORM_ORGANISATION_ROAD_FREIGHT_OPTION}
                    classes="flex-fill mr-2"
                    placeholder="Select road freight"
                    required
                    disable={preselectFreightmodes}
                  />
                )}
                {!isNewShipment && (
                  <Button
                    variant="danger"
                    className="rounded-pill h-26 ml-4"
                    label={intl.formatMessage({
                      id: "new-request.packing-list.remove",
                      defaultMessage: "Remove",
                    })}
                    disabled={fields.length <= 1 || preselectFreightmodes}
                    onClick={() => {
                      handleFreightModeSelectChange(null, index);
                      remove(index);
                    }}
                  />
                )}
              </div>
            </div>
          ))}
        {!isNewShipment && (
          <div className="mt-3">
            {fields.length < freightModes.length && state.freightModes?.request_multiple && !isLoading && (
              <Button
                disabled={preselectFreightmodes || isMultiDropQuoteRequest}
                variant="primary"
                className="rounded-pill blue-200 h-26 mb-3"
                data-testid={testIds.REQUEST_FORM_ORGANISATION_ADD_MODE_BUTTON}
                label={intl.formatMessage({
                  id: "new-request.organisational-details.addMode",
                  defaultMessage: "Add mode",
                })}
                onClick={() => append({ value: "" })}
              />
            )}
          </div>
        )}
        {get(errors, "invited_modes")?.message && (
          <div className="d-block font-b3 invalid-feedback">{get(errors, "invited_modes").message}</div>
        )}
        {hasRemovedFreightModes && (
          <Alert
            label="Removing freight mode may affect submitted quotes"
            variant="warning"
            customActions={<ButtonV2 label="Undo removal" variant="link-color" onClick={() => undoRemoval()} />}
          >
            <p className="mt-xs mb-0">
              <FormattedMessage
                id="new-request.organisational-details.removalFreightModeWarning"
                defaultMessage="Removing a freight mode will cancel any quotes already submitted using it. Please be sure you want to proceed before saving your changes."
              />
            </p>
          </Alert>
        )}
      </div>
    </div>
  );
}

export default OrganisationFormFreightMode;

OrganisationFormFreightMode.defaultProps = {
  isNewShipment: false,
  isLoadingShipmentBasic: false,
  isConsolidated: false,
};
